import { WikipediaArticle, InterestProfile, ViewSignal } from '../types';

const EN_API_URL = 'https://en.wikipedia.org/w/api.php';

// CATEGORIES PEOPLE ACTUALLY CARE ABOUT (15-30 year olds)
const INTERESTING_CATS = [
  // Business & Money
  'Billionaires', 'Companies', 'Entrepreneurs', 'Start-ups', 'Brands',
  'Cryptocurrencies', 'Stock_exchanges', 'Investment', 'Real_estate',
  
  // Sports (mainstream)
  'FIFA_World_Cup', 'UEFA_Champions_League', 'Premier_League', 'NBA', 'NFL',
  'Formula_One', 'Boxing', 'UFC', 'Olympic_Games', 'Tennis',
  
  // Athletes & Celebrities
  'Footballers', 'Basketball_players', 'Rappers', 'Actors', 'Models',
  'Influencers', 'YouTubers', 'Singers',
  
  // Fitness & Health
  'Bodybuilding', 'Fitness', 'Nutrition', 'Exercise', 'Sports_science',
  'Athletes', 'Training', 'Wellness',
  
  // Tech & Innovation
  'Smartphones', 'Social_media', 'Artificial_intelligence', 'Electric_vehicles',
  'Video_games', 'Gaming_consoles', 'Streaming_services', 'Apps',
  
  // Mental Health & Self-Development
  'Mental_health', 'Psychology', 'Meditation', 'Self-help', 'Therapy',
  'Mindfulness', 'Personal_development', 'Motivation',
  
  // Social Media & Internet Culture
  'Internet_culture', 'Viral_videos', 'Memes', 'TikTok', 'Instagram',
  'Content_creators', 'Podcasts', 'Streaming_platforms',
  
  // AI & New Technology
  'Machine_learning', 'Robotics', 'Future_technology', 'Innovation',
  'Space_technology', 'Virtual_reality', 'Augmented_reality',
  
  // Climate & Sustainability
  'Climate_change', 'Renewable_energy', 'Sustainability', 'Environmental_issues',
  'Green_technology', 'Conservation', 'Pollution',
  
  // Career & Future Choices
  'Education', 'Universities', 'Career_development', 'Remote_work',
  'Skills', 'Job_market', 'Professional_development',
  
  // Theories & Philosophy
  'Conspiracy_theories', 'Philosophy', 'Philosophical_theories', 'Scientific_theories',
  'Paradoxes', 'Thought_experiments', 'Unsolved_problems', 'Hypotheses',
  
  // Lifestyle & Culture
  'Fashion_designers', 'Luxury_brands', 'Sneakers', 'Watches', 'Cars',
  'Restaurants', 'Hotels', 'Travel_destinations',
  
  // Entertainment
  'Netflix_series', 'Marvel_Cinematic_Universe', 'Music_festivals', 'Awards',
  'Reality_television', 'Stand-up_comedians',
  
  // Cool Places
  'Cities', 'Islands', 'Beaches', 'Skyscrapers', 'Stadiums', 'Landmarks',
  
  // Interesting Topics
  'True_crime', 'Scandals', 'Controversies', 'Mysteries',

  // Science & Space
  'Scientific_discoveries', 'Nobel_laureates', 'Quantum_mechanics', 'Genetics',
  'Space_exploration', 'NASA', 'Astronauts', 'Planets', 'Black_holes', 'SpaceX',

  // History
  'World_War_II', 'Roman_Empire', 'Ancient_Egypt', 'Revolutions', 'Cold_War',

  // Animals & Nature
  'Mammals', 'Predators', 'Big_cats', 'Sharks', 'Dinosaurs', 'Marine_animals',

  // Food & Cooking
  'Cuisine', 'Fast_food', 'Celebrity_chefs', 'Chocolate', 'Coffee',

  // Music & Movies
  'Rock_music', 'Pop_music', 'Hip_hop_music', 'Music_festivals',
  'Films', 'Television_series', 'Marvel_Cinematic_Universe', 'Netflix',

  // Gaming
  'Video_games', 'Esports', 'Nintendo', 'PlayStation',

  // Art & Psychology
  'Painters', 'Art_movements', 'Contemporary_art',
  'Psychological_experiments', 'Social_psychology',

  // Medicine & Geography
  'Diseases', 'Vaccines', 'Pandemics', 'Medical_breakthroughs',
  'Volcanoes', 'Natural_disasters', 'Mountains',

  // Mythology & Cars & Military & Ocean
  'Greek_mythology', 'Norse_mythology', 'Mythological_creatures',
  'Supercars', 'Formula_One_cars', 'Classic_cars',
  'Battles', 'Special_forces', 'Military_history',
  'Shipwrecks', 'Marine_biology', 'Deep_sea'
];

const CATEGORY_THEMES: Record<string, string[]> = {
  business: ['Billionaires', 'Companies', 'Businesspeople', 'Chief_executive_officers', 'American_chief_executives', 'Venture_capital_firms', 'Private_equity_firms', 'Hedge_fund_managers', 'Forbes_lists', 'Conglomerates', 'Tech_entrepreneurs', 'Indian_billionaires', 'Chinese_billionaires', 'Female_billionaires'],
  money: ['Cryptocurrencies', 'Billionaires', 'Luxury_brands', 'Financial_services_companies', 'Stock_exchanges', 'Investment_banks', 'Cryptocurrency_exchanges', 'Decentralized_finance', 'Financial_scandals', 'Economic_bubbles'],
  football: ['Association_football', 'Footballers', 'Premier_League_players', 'La_Liga_players', 'FIFA_World_Cup', 'Serie_A_players', 'Bundesliga_players', 'Ligue_1_players', 'UEFA_Champions_League', 'Football_managers', 'Brazilian_footballers', 'Argentine_footballers', 'French_footballers', 'English_footballers', 'Spanish_footballers', 'German_footballers', 'Portuguese_footballers', 'Dutch_footballers', 'Italian_footballers', 'African_footballers', 'Football_clubs', 'Women_association_football_players', 'FIFA_World_Cup_finals', 'European_Championship_players'],
  sports: ['Basketball_players', 'National_Football_League_players', 'Formula_One', 'Mixed_martial_artists', 'Olympic_athletes', 'Tennis_players', 'Boxers', 'Golfers', 'Racing_drivers', 'Swimmers', 'Track_and_field_athletes', 'Cricket_players', 'Rugby_union_players', 'Ice_hockey_players', 'Baseball_players', 'Gymnasts', 'Cyclists', 'Skiers', 'Olympic_gold_medalists', 'Sports_controversies'],
  fitness: ['Bodybuilders', 'Physical_exercise', 'Nutrition', 'Health', 'Sports_nutrition', 'Strength_athletes', 'Powerlifters', 'Olympic_weightlifters', 'Martial_arts', 'Yoga', 'CrossFit', 'Physical_fitness', 'Dietary_supplements', 'Diets', 'Sports_medicine'],
  celebrities: ['American_rappers', 'American_actors', 'American_singers', 'YouTubers', 'Instagram', 'British_actors', 'Film_directors', 'Record_producers', 'Supermodels', 'Television_presenters', 'Comedians', 'Talk_show_hosts', 'Grammy_Award_winners', 'Academy_Award_winners', 'Reality_television_participants', 'Korean_pop_singers', 'British_singers'],
  tech: ['Smartphones', 'Artificial_intelligence', 'Video_games', 'Social_media', 'Technology_companies', 'Computer_hardware', 'Programming_languages', 'Cybersecurity', 'Cloud_computing', 'Open-source_software', 'Mobile_apps', 'Silicon_Valley', 'Tech_companies_of_China', 'Wearable_technology', 'Quantum_computing'],
  mental_health: ['Mental_health', 'Psychology', 'Meditation', 'Psychotherapy', 'Cognitive_science', 'Neuroscience', 'Anxiety_disorders', 'Mood_disorders', 'Sleep', 'Stress', 'Positive_psychology', 'Behavioral_therapy', 'Psychiatry', 'Emotional_intelligence'],
  internet: ['Internet_culture', 'Social_media', 'YouTube', 'TikTok', 'Viral_videos', 'Internet_memes', 'Podcasts', 'Streaming_media', 'Online_communities', 'Digital_media', 'Twitch_streamers', 'Internet_celebrities', 'Web_culture', 'Online_advertising'],
  ai_future: ['Artificial_intelligence', 'Robotics', 'Machine_learning', 'Emerging_technologies', 'Nanotechnology', 'Biotechnology', 'Space_exploration', 'Transhumanism', 'Futurology', 'Autonomous_vehicles', 'Neural_networks', 'Computer_vision', 'Natural_language_processing', 'SpaceX', 'Mars_exploration'],
  climate: ['Climate_change', 'Renewable_energy', 'Sustainability', 'Environmental_issues', 'Solar_energy', 'Wind_power', 'Electric_vehicles', 'Deforestation', 'Ocean_pollution', 'Endangered_species', 'National_parks', 'Environmental_organizations', 'Carbon_emissions', 'Green_building', 'Water_pollution'],
  career: ['Universities_and_colleges', 'Education', 'Business_schools', 'Scholarships', 'Online_education', 'Professional_certifications', 'Internships', 'Entrepreneurship', 'Leadership', 'Public_speaking', 'Ivy_League', 'Technical_universities', 'Study_abroad'],
  theories: ['Conspiracy_theories', 'Philosophy', 'Paradoxes', 'Hypotheses', 'Philosophical_movements', 'Ethics', 'Logic', 'Metaphysics', 'Epistemology', 'Ancient_Greek_philosophers', 'Thought_experiments', 'Unsolved_problems', 'Scientific_controversies', 'Pseudoscience', 'Urban_legends'],
  lifestyle: ['Fashion_designers', 'Luxury_brands', 'Sports_cars', 'Luxury_goods', 'Haute_couture', 'Watches', 'Perfumes', 'Jewellery', 'Interior_design', 'Architecture', 'Supercars', 'Yachts', 'Michelin_Guide_restaurants', 'Fashion_models', 'Street_fashion'],
  entertainment: ['American_films', 'Television_series', 'Music_festivals', 'Netflix', 'Anime', 'Marvel_Cinematic_Universe', 'Horror_films', 'Science_fiction_films', 'Video_game_franchises', 'Board_games', 'Amusement_parks', 'Broadway_musicals', 'Documentary_films', 'Stand-up_comedy', 'K-drama'],
  travel: ['Cities', 'Tourist_attractions', 'World_Heritage_Sites', 'Islands', 'Beaches', 'Mountains', 'National_parks', 'Capitals', 'Skyscrapers', 'Castles', 'Ancient_cities', 'Resorts', 'Airlines', 'Bridges', 'UNESCO_Intangible_Cultural_Heritage'],
  drama: ['True_crime', 'Scandals', 'Controversies', 'Unsolved_mysteries', 'Serial_killers', 'Heists', 'Espionage', 'Political_scandals', 'Corporate_scandals', 'Cults', 'Disappearances', 'Cold_cases', 'Fraudsters', 'Prison_escapes', 'Notorious_criminals'],
  science: ['Physics', 'Chemistry', 'Biology', 'Scientific_discoveries', 'Nobel_laureates', 'Experiments', 'Scientific_theories', 'Genetics', 'Quantum_mechanics', 'Astronomy', 'Mathematics', 'Evolution', 'Periodic_table', 'Science_museums'],
  space: ['Space_exploration', 'NASA', 'Astronauts', 'Planets', 'Galaxies', 'Black_holes', 'Space_missions', 'Rockets', 'SpaceX', 'International_Space_Station', 'Telescopes', 'Satellites', 'Mars_exploration', 'Moon_landings'],
  history: ['Ancient_history', 'World_War_II', 'World_War_I', 'Roman_Empire', 'Medieval_history', 'Ancient_Egypt', 'Revolutions', 'Cold_War', 'Historical_figures', 'Empires', 'Civilizations', 'Renaissance', 'Industrial_Revolution', 'Colonial_history'],
  animals: ['Animals', 'Mammals', 'Birds', 'Reptiles', 'Marine_animals', 'Endangered_species', 'Predators', 'Insects', 'Primates', 'Big_cats', 'Sharks', 'Whales', 'Dogs', 'Dinosaurs'],
  food: ['Cuisine', 'Restaurants', 'Cooking', 'Fast_food', 'Desserts', 'Beverages', 'Wine', 'Coffee', 'Chocolate', 'Spices', 'Street_food', 'Michelin_Guide', 'Celebrity_chefs', 'Food_culture'],
  music: ['Musicians', 'Rock_music', 'Pop_music', 'Hip_hop_music', 'Electronic_music', 'Music_albums', 'Music_genres', 'Bands', 'Singers', 'Grammy_Award_winners', 'Music_festivals', 'Songwriters', 'Music_producers', 'Classical_composers'],
  movies_tv: ['Films', 'Television_series', 'Film_directors', 'Actors', 'Academy_Awards', 'Netflix', 'HBO', 'Animation', 'Horror_films', 'Science_fiction_films', 'Marvel_Cinematic_Universe', 'Disney', 'Documentaries', 'Film_franchises'],
  gaming: ['Video_games', 'Video_game_developers', 'Esports', 'Nintendo', 'PlayStation', 'Xbox', 'PC_games', 'Mobile_games', 'Game_designers', 'Video_game_franchises', 'Multiplayer_games', 'Role-playing_video_games', 'Action_video_games', 'Indie_games'],
  art: ['Painters', 'Sculptures', 'Art_movements', 'Museums', 'Contemporary_art', 'Renaissance_art', 'Photography', 'Art_galleries', 'Paintings', 'Graffiti', 'Street_art', 'Digital_art', 'Illustration', 'Art_exhibitions'],
  psychology: ['Psychology', 'Cognitive_science', 'Behavioral_science', 'Psychological_experiments', 'Social_psychology', 'Developmental_psychology', 'Personality', 'Perception', 'Memory', 'Learning', 'Intelligence', 'Emotions', 'Psychological_disorders', 'Psychologists'],
  medicine: ['Medicine', 'Diseases', 'Surgery', 'Vaccines', 'Pharmacology', 'Medical_research', 'Hospitals', 'Epidemics', 'Pandemics', 'Medical_devices', 'Nobel_laureates_in_Physiology_or_Medicine', 'Anatomy', 'Medical_breakthroughs', 'Public_health'],
  geography: ['Geography', 'Countries', 'Mountains', 'Rivers', 'Lakes', 'Volcanoes', 'Deserts', 'Continents', 'Natural_disasters', 'Geological_formations', 'Caves', 'Waterfalls', 'Archipelagos', 'Extreme_points_of_Earth'],
  mythology: ['Mythology', 'Greek_mythology', 'Norse_mythology', 'Egyptian_mythology', 'Roman_mythology', 'Folklore', 'Legends', 'Mythological_creatures', 'Gods', 'Ancient_religions', 'Celtic_mythology', 'Japanese_mythology', 'Hindu_mythology', 'Creation_myths'],
  cars: ['Cars', 'Automobiles', 'Sports_cars', 'Electric_vehicles', 'Car_manufacturers', 'Formula_One_cars', 'Luxury_vehicles', 'Supercars', 'Auto_racing', 'Concept_cars', 'Classic_cars', 'Motorcycles', 'Automotive_industry', 'Car_design'],
  military: ['Military_history', 'Battles', 'Wars', 'Military_vehicles', 'Weapons', 'Armies', 'Navies', 'Air_forces', 'Special_forces', 'Military_leaders', 'Military_operations', 'Military_technology', 'Tanks', 'Aircraft_carriers'],
  ocean: ['Oceans', 'Marine_biology', 'Coral_reefs', 'Deep_sea', 'Marine_animals', 'Ships', 'Shipwrecks', 'Ocean_exploration', 'Submarines', 'Oceanography', 'Tides', 'Sea_creatures', 'Maritime_history', 'Lighthouses']
};

// TRENDING/POPULAR - VARIED MIX
const TRENDING_CATS = [
  'Billionaires', 'Premier_League', 'Artificial_intelligence',
  'Mental_health', 'Cryptocurrencies', 'Climate_change',
  'Social_media', 'NBA', 'Electric_vehicles',
  'Internet_culture', 'True_crime', 'Fitness',
  'Conspiracy_theories', 'Philosophy', 'Bodybuilding',
  'Space_exploration', 'Scientific_discoveries', 'World_War_II',
  'Dinosaurs', 'Video_games', 'Films', 'Hip_hop_music',
  'Greek_mythology', 'Pandemics', 'Volcanoes',
  'Supercars', 'Shipwrecks', 'Special_forces'
];

// SEARCH TERMS PER THEME — Wikipedia search gives WAY more variety than categories alone
const THEME_SEARCH_TERMS: Record<string, string[]> = {
  business: ['billionaire entrepreneur biography', 'tech company founder', 'fortune 500 corporation', 'venture capital startup', 'corporate merger acquisition', 'business empire history', 'wall street finance', 'retail brand story', 'conglomerate corporation', 'private equity firm', 'IPO stock market listing', 'female CEO executive', 'banking institution history', 'franchise business model', 'trade industry magnate'],
  money: ['cryptocurrency blockchain exchange', 'stock market crash history', 'financial crisis banking', 'investment strategy fund', 'economic bubble speculation', 'financial fraud scandal', 'central bank monetary policy', 'hedge fund manager billionaire', 'real estate investment property', 'ponzi scheme financial fraud', 'wealth inequality economics', 'digital payment fintech', 'insurance company history', 'tax haven offshore finance', 'commodity gold trading'],
  football: ['footballer player biography career', 'premier league season history', 'champions league final match', 'world cup tournament football', 'football club history founded', 'la liga spanish footballer', 'serie a italian football', 'bundesliga german footballer', 'ligue 1 french football', 'football manager coach biography', 'women football player career', 'football transfer record fee', 'international football team history', 'football derby rivalry classic', 'african footballer biography', 'south american football legend', 'football goalkeeper biography', 'youth football academy player', 'football stadium arena history', 'asian football player career'],
  sports: ['olympic gold medal athlete biography', 'NBA basketball player career stats', 'formula one racing driver biography', 'boxing world champion biography', 'tennis grand slam winner career', 'UFC MMA fighter biography career', 'cricket test match player biography', 'rugby world cup player career', 'ice hockey NHL player biography', 'baseball MLB player career', 'athletics world record holder', 'swimming olympic champion biography', 'golf major tournament winner', 'cycling tour champion biography', 'extreme sport athlete adventure'],
  fitness: ['bodybuilding champion competition history', 'CrossFit athlete games competition', 'Olympic weightlifting record holder', 'martial arts master biography', 'yoga practice tradition history', 'sports nutrition supplement science', 'powerlifting strength record', 'fitness industry company history', 'exercise science physiology research', 'running marathon ultramarathon champion', 'combat sport training technique', 'strongman competition athlete biography', 'pilates exercise method history', 'physical therapy rehabilitation sport', 'calisthenics street workout'],
  celebrities: ['hollywood actor film career biography', 'pop singer musician biography career', 'rapper hip hop artist discography', 'youtube creator famous channel', 'reality television star biography', 'film director movies filmography', 'grammy award winning musician', 'oscar winning actor actress', 'supermodel fashion career biography', 'kpop korean pop group member', 'comedian stand-up career biography', 'talk show host television career', 'voice actor animation career', 'music producer songwriter biography', 'child actor star biography career'],
  tech: ['technology company silicon valley history', 'smartphone mobile device history', 'artificial intelligence company research', 'video game developer studio history', 'cybersecurity data breach incident', 'social media platform company history', 'cloud computing technology company', 'programming language software history', 'quantum computing research breakthrough', 'electric vehicle car technology', 'semiconductor chip manufacturer', 'space technology satellite company', 'mobile application app startup', 'operating system software history', 'tech startup unicorn company'],
  mental_health: ['psychology research discovery study', 'mental health treatment therapy history', 'anxiety disorder therapy treatment', 'meditation mindfulness tradition practice', 'neuroscience brain research discovery', 'cognitive behavioral therapy treatment', 'psychiatric treatment history medicine', 'emotional intelligence research theory', 'sleep disorder science research', 'stress management psychology research', 'addiction recovery treatment program', 'autism spectrum research study', 'personality disorder psychology study', 'child development psychology research', 'positive psychology wellbeing research'],
  internet: ['internet culture phenomenon viral', 'social media platform influence history', 'podcast popular show history origin', 'streaming platform entertainment service', 'internet meme origin viral culture', 'twitch streaming gamer community', 'online community forum history culture', 'digital content creation industry', 'web technology history innovation', 'internet startup company history', 'viral challenge social media trend', 'online marketplace ecommerce history', 'blog website media platform', 'internet privacy security controversy', 'social network company history'],
  ai_future: ['artificial intelligence research breakthrough', 'robot automation manufacturing future', 'space exploration NASA mission', 'nanotechnology research science study', 'biotechnology medical research breakthrough', 'autonomous vehicle self driving car', 'neural network deep learning AI', 'Mars exploration space mission', 'virtual reality VR technology', 'drone UAV technology aviation', 'genetic engineering CRISPR research', '3D printing manufacturing technology', 'brain computer interface research', 'fusion energy research reactor', 'satellite constellation space internet'],
  climate: ['climate change global warming impact', 'renewable energy solar installation', 'wind farm energy production project', 'ocean pollution plastic waste crisis', 'endangered species conservation effort', 'national park nature reserve wildlife', 'deforestation rainforest tropical loss', 'carbon emission greenhouse gas climate', 'sustainable development ecology project', 'glacier melting ice arctic', 'coral reef ecosystem marine', 'water crisis drought disaster', 'wildfire climate change disaster', 'biodiversity habitat loss extinction', 'electric vehicle green transportation'],
  career: ['university prestigious education ranking', 'scholarship academic program education', 'business school MBA program', 'online learning education platform', 'entrepreneurship business success story', 'leadership management theory practice', 'professional certification career development', 'internship career program opportunity', 'study abroad university exchange', 'academic research institution history', 'STEM education science technology', 'vocational training education program', 'public speaking communication skill', 'mentorship career development program', 'fellowship research academic grant'],
  theories: ['conspiracy theory famous popular', 'philosophy thought experiment paradox', 'scientific theory discovery breakthrough', 'unsolved mystery famous case', 'paradox logical philosophical famous', 'urban legend myth folklore popular', 'pseudoscience controversy debate famous', 'ancient Greek Roman philosophy', 'ethical dilemma moral philosophy', 'mathematical theorem proof famous', 'quantum physics theory discovery', 'simulation hypothesis theory argument', 'game theory mathematics strategy', 'chaos theory complexity science', 'existentialism philosophy movement'],
  lifestyle: ['luxury fashion brand designer house', 'supercar exotic sports car model', 'luxury watch brand Swiss timepiece', 'haute couture fashion show designer', 'michelin star restaurant famous chef', 'superyacht luxury mega vessel', 'famous architecture building landmark', 'interior design style movement', 'perfume fragrance luxury house', 'street fashion style urban trend', 'jewelry diamond luxury brand', 'luxury hotel resort destination', 'fine wine vineyard winery estate', 'gourmet cuisine famous dish', 'art gallery auction famous painting'],
  entertainment: ['television series popular award winning', 'marvel superhero film movie', 'anime manga Japanese series popular', 'horror film classic movie famous', 'music festival concert event famous', 'video game franchise popular series', 'broadway musical theater production', 'documentary film award winning famous', 'comedy show stand-up special', 'science fiction film classic movie', 'animated film studio production', 'reality competition show television', 'film franchise sequel series', 'iconic music album artist', 'theme park amusement attraction'],
  travel: ['famous tourist attraction destination', 'UNESCO world heritage site historic', 'tropical island paradise destination', 'famous beach resort coastal', 'mountain peak highest climbing', 'ancient ruins archaeological site', 'castle palace historic landmark', 'tallest building skyscraper city', 'airline aviation company history', 'cultural festival tradition celebration', 'cave natural wonder geological', 'waterfall scenic famous nature', 'desert landscape national park', 'volcano geological natural wonder', 'historic bridge famous landmark'],
  drama: ['true crime famous murder case', 'political scandal government controversy', 'famous bank heist robbery history', 'serial killer criminal case history', 'espionage spy intelligence operation', 'corporate fraud scandal investigation', 'cult dangerous organization history', 'missing person disappearance case', 'prison escape notorious inmate', 'famous trial court case verdict', 'assassination political history leader', 'war crime trial prosecution', 'kidnapping famous ransom case', 'smuggling trafficking criminal operation', 'wrongful conviction innocence case'],
  science: ['scientific discovery breakthrough Nobel', 'physics experiment famous result', 'chemistry element discovery periodic table', 'biology evolution species discovery', 'quantum mechanics physics theory', 'genetics DNA genome research', 'astronomy star planet discovery', 'mathematics theorem proof famous', 'Nobel Prize laureate science', 'particle physics accelerator CERN', 'climate science research study', 'paleontology fossil discovery ancient', 'geology earthquake tectonic plate', 'microbiology virus bacteria discovery', 'scientific instrument invention laboratory'],
  space: ['space exploration NASA mission', 'astronaut biography space mission', 'planet solar system discovery', 'galaxy universe astronomy observation', 'black hole discovery theory', 'Mars mission rover exploration', 'SpaceX rocket launch reusable', 'International Space Station ISS', 'telescope observatory discovery', 'satellite space technology orbit', 'moon landing Apollo mission', 'space shuttle program history', 'comet asteroid space object', 'exoplanet discovery habitable zone', 'space probe deep mission'],
  history: ['ancient civilization empire history', 'World War II battle campaign', 'World War I trench warfare', 'Roman Empire ancient history', 'medieval castle knight kingdom', 'ancient Egypt pharaoh pyramid', 'revolution independence freedom history', 'Cold War espionage nuclear', 'famous historical figure biography', 'empire rise fall dynasty', 'Renaissance art culture period', 'Industrial Revolution invention factory', 'colonial history exploration voyage', 'Viking Norse exploration raid', 'ancient Greek civilization democracy'],
  animals: ['animal species wildlife nature', 'mammal predator apex hunter', 'bird species migration flight', 'reptile crocodile snake lizard', 'marine animal ocean creature', 'endangered species conservation protection', 'predator hunting behavior wild', 'insect butterfly beetle colony', 'primate ape monkey species', 'big cat lion tiger leopard', 'shark species ocean predator', 'whale dolphin marine mammal', 'dog breed canine companion', 'dinosaur prehistoric fossil extinct', 'venomous dangerous animal species'],
  food: ['cuisine traditional national dish', 'famous restaurant chef Michelin', 'cooking technique culinary method', 'fast food chain restaurant history', 'dessert pastry chocolate sweet', 'wine region vineyard grape', 'coffee culture origin bean', 'chocolate history production cacao', 'spice herb seasoning origin', 'street food market culture', 'food history origin traditional', 'celebrity chef television cooking', 'sushi Japanese cuisine tradition', 'pizza Italian food history', 'barbecue grill smoking meat'],
  music: ['musician artist biography career', 'rock band classic album', 'pop singer songwriter hit', 'hip hop rapper album career', 'electronic music DJ producer', 'music album iconic influential', 'music genre history origin', 'famous band group members', 'Grammy Award winning album', 'music festival concert famous', 'songwriter composer legendary career', 'classical music composer symphony', 'jazz musician instrument legend', 'country music singer artist', 'punk rock alternative band'],
  movies_tv: ['film movie classic award', 'television series popular acclaimed', 'film director biography filmography', 'actor actress award career', 'Academy Award Oscar winning film', 'Netflix series original production', 'HBO show series premium', 'animated film studio production', 'horror movie classic cult', 'science fiction film classic', 'Marvel superhero movie universe', 'Disney film animation classic', 'documentary film award winning', 'film franchise sequel series', 'television sitcom comedy show'],
  gaming: ['video game popular franchise', 'game developer studio history', 'esports tournament competition pro', 'Nintendo game console classic', 'PlayStation exclusive game console', 'Xbox game series Microsoft', 'PC game strategy simulation', 'mobile game popular app', 'game designer creator famous', 'game franchise series sequel', 'multiplayer online game community', 'RPG role playing game classic', 'action adventure game popular', 'indie game developer studio', 'retro classic arcade game'],
  art: ['painter artist famous masterpiece', 'sculpture famous sculptor artwork', 'art movement style period', 'museum gallery famous collection', 'contemporary modern art exhibition', 'Renaissance painting artist classic', 'photography photographer famous image', 'art gallery auction record', 'famous painting artwork history', 'graffiti street art urban', 'digital art illustration design', 'art exhibition biennale show', 'portrait landscape still life', 'impressionism expressionism cubism art', 'art collector patron museum'],
  psychology: ['psychology study experiment famous', 'cognitive science brain research', 'behavioral psychology experiment theory', 'psychological experiment Stanford Milgram', 'social psychology group behavior', 'developmental psychology child growth', 'personality theory type trait', 'perception illusion visual brain', 'memory recall forgetting study', 'learning theory conditioning behavior', 'intelligence IQ testing measure', 'emotion theory feeling affect', 'psychological disorder condition study', 'psychologist biography theory career', 'forensic psychology criminal profiling'],
  medicine: ['medicine treatment cure disease', 'disease infection symptom treatment', 'surgery technique procedure medical', 'vaccine immunization disease prevention', 'pharmaceutical drug medication treatment', 'medical research breakthrough study', 'hospital institution health care', 'epidemic outbreak disease spread', 'pandemic global health crisis', 'medical device instrument technology', 'anatomy human body organ', 'public health campaign initiative', 'antibiotic resistance bacteria infection', 'cancer research treatment therapy', 'medical pioneer physician biography'],
  geography: ['country nation geography culture', 'mountain peak highest climbing', 'river longest flowing geography', 'volcano eruption geological active', 'desert landscape arid region', 'continent geography landmass', 'natural disaster earthquake tsunami', 'geological formation rock canyon', 'cave system underground cavern', 'waterfall famous scenic nature', 'island archipelago tropical ocean', 'glacier ice sheet polar', 'rainforest tropical jungle ecosystem', 'canyon gorge geological erosion', 'peninsula cape geographical feature'],
  mythology: ['Greek mythology god hero', 'Norse mythology Viking legend', 'Egyptian mythology ancient god', 'Roman mythology deity temple', 'folklore legend fairy tale', 'mythological creature monster beast', 'Celtic mythology druid legend', 'Japanese mythology spirit kami', 'Hindu mythology deity epic', 'creation myth origin story', 'dragon phoenix unicorn myth', 'underworld afterlife mythology', 'Aztec Mayan mythology ancient', 'hero quest journey mythology', 'trickster god mythology folklore'],
  cars: ['car automobile classic model', 'sports car performance speed', 'electric vehicle EV battery', 'car manufacturer brand history', 'Formula One race car team', 'luxury car brand premium', 'supercar hypercar exotic fast', 'auto racing motorsport history', 'concept car prototype design', 'classic car vintage restoration', 'motorcycle brand model racing', 'automotive industry innovation', 'car design engineer history', 'rally racing off road', 'muscle car American classic'],
  military: ['military history war battle', 'famous battle historical warfare', 'war conflict campaign strategy', 'military vehicle tank aircraft', 'weapon firearm artillery history', 'army regiment division force', 'navy warship fleet history', 'air force fighter jet plane', 'special forces elite unit', 'military leader general commander', 'military operation strategy tactics', 'military technology innovation weapon', 'tank armored vehicle warfare', 'aircraft carrier warship navy', 'siege fortress castle defense'],
  ocean: ['ocean sea marine exploration', 'marine biology sea life', 'coral reef tropical marine', 'deep sea creature abyss', 'marine animal species ocean', 'ship vessel sailing history', 'shipwreck famous underwater wreck', 'ocean exploration expedition deep', 'submarine vessel underwater navy', 'oceanography current tide wave', 'sea creature jellyfish octopus', 'maritime history trade route', 'lighthouse coastal navigation beacon', 'whale shark dolphin marine', 'underwater archaeology treasure wreck']
};

const BORING_PATTERNS = [
  /^\d{3,4}$/, 
  /^\d{3,4}s$/,
  /^list of/i,
  /^index of/i,
  /\(disambiguation\)/i,
  /^template:/i,
  /\d{4}–\d{4}$/,
  /^category:/i,
];

const BORING_KEYWORDS = [
  'census', 'population statistics', 'administrative division', 
  'postal code', 'electoral district', 'municipality',
  'bibliography', 'discography', 'filmography' // These are usually boring lists
];

const isEngaging = (article: any): boolean => {
  if (!article.thumbnail || !article.thumbnail.source) return false;
  if (!article.extract || article.extract.length < 100) return false;
  
  if (BORING_PATTERNS.some(pattern => pattern.test(article.title))) return false;
  
  const titleLower = article.title.toLowerCase();
  const boringCount = BORING_KEYWORDS.filter(w => titleLower.includes(w)).length;
  if (boringCount >= 2) return false;
  
  // Skip pure year articles
  if (/^\d+$/.test(article.title.trim())) return false;
  
  const score = calculateInterestScore(article);
  return score >= 2; // Lower threshold for more variety
};

const calculateInterestScore = (article: any): number => {
  let score = 0;
  
  // Image quality matters
  if (article.thumbnail) {
    score += 2;
    if (article.thumbnail.width >= 400) score += 1;
  }
  
  // Good length
  const extractLen = article.extract?.length || 0;
  if (extractLen > 150 && extractLen < 800) score += 2;
  else if (extractLen >= 800) score += 1;
  
  // Has description
  if (article.pageprops?.['wikibase-description']) score += 1;
  
  const text = `${article.title} ${article.extract || ''} ${article.pageprops?.['wikibase-description'] || ''}`.toLowerCase();
  
  // KEYWORDS PEOPLE CARE ABOUT
  const engagingWords = [
    // Business & Money
    'billionaire', 'ceo', 'founder', 'millionaire', 'net worth', 'valuation',
    'startup', 'ipo', 'acquisition', 'deal', 'investment', 'profit', 'revenue',
    'bitcoin', 'crypto', 'stock', 'market', 'trading', 'wealth',
    
    // Sports
    'championship', 'world cup', 'final', 'trophy', 'champion', 'winner',
    'record', 'goal', 'scored', 'mvp', 'player', 'transfer', 'contract',
    'league', 'tournament', 'medal', 'victory', 'legendary',
    
    // Fitness & Health
    'bodybuilding', 'fitness', 'workout', 'training', 'muscle', 'strength',
    'nutrition', 'diet', 'protein', 'gym', 'exercise', 'athletic',
    
    // Celebrities & Fame
    'famous', 'celebrity', 'star', 'icon', 'award', 'grammy', 'oscar',
    'viral', 'trending', 'instagram', 'followers', 'influencer',
    
    // Mental Health & Self-Development
    'mental health', 'anxiety', 'depression', 'therapy', 'mindfulness',
    'meditation', 'psychology', 'self-help', 'motivation', 'personal growth',
    'wellness', 'self-care', 'emotional', 'resilience',
    
    // Theories & Philosophy
    'theory', 'paradox', 'philosophy', 'hypothesis', 'conspiracy',
    'thought experiment', 'unsolved', 'mystery', 'phenomenon', 'explained',
    
    // Social Media & Internet
    'tiktok', 'youtube', 'podcast', 'viral', 'meme', 'content creator',
    'streamer', 'social media', 'platform', 'algorithm',
    
    // AI & Technology
    'artificial intelligence', 'ai', 'machine learning', 'robot', 'automation',
    'innovation', 'breakthrough', 'future', 'technology', 'digital',
    
    // Climate & Sustainability
    'climate change', 'global warming', 'renewable', 'sustainable',
    'environment', 'carbon', 'pollution', 'conservation', 'green energy',
    
    // Career & Education
    'university', 'degree', 'career', 'job', 'skill', 'remote work',
    'entrepreneur', 'professional', 'learning', 'education',
    
    // Luxury & Lifestyle
    'luxury', 'expensive', 'exclusive', 'premium', 'brand', 'designer',
    'ferrari', 'lamborghini', 'rolex', 'mansion', 'yacht',
    
    // Drama & Interest
    'scandal', 'controversial', 'shocking', 'dramatic', 'rivalry',
    'feud', 'lawsuit', 'fired', 'resigned', 'banned', 'arrested',
    
    // Achievement
    'world record', 'fastest', 'biggest', 'best', 'top', 'greatest',
    'youngest', 'first', 'historic', 'unprecedented',

    // Science & Space
    'experiment', 'discovery', 'research', 'quantum', 'particle', 'atom',
    'nasa', 'astronaut', 'planet', 'galaxy', 'black hole', 'mars',
    'rocket', 'spacex', 'telescope', 'orbit', 'comet', 'asteroid',

    // History
    'ancient', 'empire', 'civilization', 'pharaoh', 'medieval', 'kingdom',
    'revolution', 'war', 'battle', 'conquest', 'dynasty', 'viking',

    // Animals
    'species', 'predator', 'endangered', 'wildlife', 'dinosaur', 'shark',
    'whale', 'lion', 'tiger', 'eagle', 'dolphin', 'venomous',

    // Food
    'cuisine', 'recipe', 'chef', 'restaurant', 'michelin', 'dish',
    'chocolate', 'coffee', 'wine', 'sushi', 'pizza', 'bbq',

    // Music
    'album', 'concert', 'band', 'songwriter', 'genre', 'hit song',
    'rock', 'pop', 'hip hop', 'jazz', 'symphony', 'guitar',

    // Movies & TV
    'film', 'movie', 'director', 'oscar', 'series', 'animated',
    'horror', 'thriller', 'documentary', 'franchise', 'sequel',

    // Gaming
    'game', 'esports', 'console', 'nintendo', 'playstation', 'xbox',
    'multiplayer', 'rpg', 'indie game', 'arcade',

    // Art
    'painting', 'sculptor', 'museum', 'gallery', 'masterpiece',
    'renaissance', 'portrait', 'exhibition', 'graffiti',

    // Psychology
    'cognitive', 'behavior', 'personality', 'perception', 'emotion',
    'intelligence', 'disorder', 'experiment', 'memory',

    // Medicine
    'vaccine', 'disease', 'surgery', 'cure', 'treatment', 'epidemic',
    'pandemic', 'antibiotic', 'cancer', 'dna', 'genome',

    // Geography
    'volcano', 'earthquake', 'tsunami', 'mountain', 'canyon',
    'glacier', 'desert', 'rainforest', 'waterfall',

    // Mythology
    'mythology', 'legend', 'folklore', 'dragon', 'god', 'hero',
    'myth', 'mythical', 'creature', 'norse', 'greek',

    // Cars
    'supercar', 'motorsport', 'racing', 'engine', 'horsepower',
    'turbo', 'formula one', 'rally', 'mustang', 'porsche',

    // Military
    'military', 'army', 'navy', 'special forces', 'tank',
    'aircraft carrier', 'sniper', 'siege', 'strategy', 'commander',

    // Ocean
    'ocean', 'marine', 'coral reef', 'shipwreck', 'submarine',
    'deep sea', 'lighthouse', 'underwater', 'abyss', 'diving'
  ];
  
  const engagingCount = engagingWords.filter(w => text.includes(w)).length;
  score += Math.min(engagingCount * 1.5, 10);
  
  return score;
};

const fetchViaGenerator = async (generatorParams: Record<string, string>): Promise<WikipediaArticle[]> => {
  const params = new URLSearchParams({
    action: 'query',
    prop: 'pageimages|extracts|info|pageprops',
    piprop: 'thumbnail',
    pithumbsize: '1200',
    exintro: '1',
    explaintext: '1',
    exchars: '500', // Shorter extracts for quick reading
    inprop: 'url',
    format: 'json',
    origin: '*',
    ...generatorParams
  });

  try {
    const response = await fetch(`${EN_API_URL}?${params.toString()}`, {
      signal: AbortSignal.timeout(5000)
    });
    const data = await response.json();
    if (!data.query || !data.query.pages) return [];

    return Object.values(data.query.pages)
      .map((p: any) => ({
        pageid: p.pageid,
        title: p.title,
        displaytitle: p.title,
        thumbnail: p.thumbnail,
        extract: p.extract || '',
        extract_html: p.extract || '',
        description: p.pageprops?.['wikibase-description'] || '',
        content_urls: {
          desktop: { page: p.fullurl || `https://en.wikipedia.org/wiki/${encodeURIComponent(p.title)}` },
          mobile: { page: (p.fullurl || `https://en.wikipedia.org/wiki/${encodeURIComponent(p.title)}`).replace('en.wikipedia', 'en.m.wikipedia') }
        },
        _interestScore: calculateInterestScore(p)
      }))
      .filter(isEngaging)
      .sort(() => Math.random() - 0.5); // Shuffle instead of always picking top-scored
  } catch (e) {
    console.error('Wiki fetch failed:', e);
    return [];
  }
};

const getUserTopCategories = (profile?: InterestProfile): string[] => {
  if (!profile || Object.keys(profile).length === 0) {
    return TRENDING_CATS.slice(0, 6);
  }
  
  const sorted = Object.entries(profile)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([cat]) => cat);
  
  return sorted;
};

const getRelatedCategories = (categories: string[]): string[] => {
  const related = new Set<string>();
  
  categories.forEach(cat => {
    for (const [theme, cats] of Object.entries(CATEGORY_THEMES)) {
      if (cats.includes(cat)) {
        cats.forEach(c => related.add(c));
      }
    }
  });
  
  return Array.from(related);
};

export const fetchArticleBatch = async (
  count: number,
  excludeIds: Set<number>,
  mode: 'feed' | 'search' | 'explore',
  params: { query?: string, offset?: number, profile?: InterestProfile, userInterests?: string[] }
): Promise<{ articles: WikipediaArticle[], nextOffset?: number }> => {
  
  if (mode === 'search' && params.query) {
    const articles = await fetchViaGenerator({
      generator: 'search',
      gsrsearch: params.query,
      gsrlimit: '50',
      gsroffset: (params.offset || 0).toString()
    });
    return { articles: articles.filter(a => !excludeIds.has(a.pageid)), nextOffset: (params.offset || 0) + 50 };
  }

  let gathered: WikipediaArticle[] = [];
  let attempts = 0;

  while (gathered.length < count && attempts < 5) {
    attempts++;

    const promises: Promise<WikipediaArticle[]>[] = [];

    // Determine which themes to use (explore = opposite of user interests)
    const isExplore = mode === 'explore';
    const allThemes = Object.keys(CATEGORY_THEMES);
    const activeThemes = isExplore
      ? allThemes.filter(t => !(params.userInterests || []).includes(t))
      : (params.userInterests || []);

    // STRATEGY 1: WIKIPEDIA SEARCH (best variety — searches millions of articles)
    if (activeThemes.length > 0) {
      const allSearchTerms: string[] = [];
      activeThemes.forEach(theme => {
        const terms = THEME_SEARCH_TERMS[theme];
        if (terms) allSearchTerms.push(...terms);
      });

      if (allSearchTerms.length > 0) {
        const shuffledTerms = [...allSearchTerms].sort(() => Math.random() - 0.5);
        // Pick 3 different search terms for massive variety
        for (let i = 0; i < Math.min(3, shuffledTerms.length); i++) {
          promises.push(
            fetchViaGenerator({
              generator: 'search',
              gsrsearch: shuffledTerms[i],
              gsrlimit: '20',
              gsroffset: Math.floor(Math.random() * 80).toString()
            })
          );
        }
      }
    }

    // STRATEGY 2: CATEGORY BROWSING (themed content, pages only)
    {
      let categoriesToUse: string[] = [];

      if (activeThemes.length > 0) {
        activeThemes.forEach(theme => {
          const themeCats = CATEGORY_THEMES[theme];
          if (themeCats) categoriesToUse.push(...themeCats);
        });
      }

      if (categoriesToUse.length === 0) {
        const userTopCats = getUserTopCategories(params.profile);
        const relatedCats = getRelatedCategories(userTopCats);
        categoriesToUse = [...userTopCats, ...relatedCats];
      }

      const shuffled = [...categoriesToUse].sort(() => Math.random() - 0.5);
      const selectedCats = new Set<string>();
      for (let i = 0; i < shuffled.length && selectedCats.size < 4; i++) {
        selectedCats.add(shuffled[i]);
      }

      selectedCats.forEach(cat => {
        promises.push(
          fetchViaGenerator({
            generator: 'categorymembers',
            gcmtitle: `Category:${cat}`,
            gcmlimit: '50',
            gcmtype: 'page',
            gcmsort: Math.random() > 0.5 ? 'timestamp' : 'sortkey',
            gcmdir: Math.random() > 0.5 ? 'asc' : 'desc',
            gcmoffset: Math.floor(Math.random() * 200).toString()
          })
        );
      });
    }

    // STRATEGY 3: TRULY RANDOM WIKIPEDIA ARTICLES (real discovery)
    promises.push(
      fetchViaGenerator({
        generator: 'random',
        grnnamespace: '0',
        grnlimit: '20'
      })
    );

    const results = await Promise.all(promises);
    const combined = results.flat();

    const filtered = combined.filter(a =>
      !excludeIds.has(a.pageid) &&
      !gathered.some(g => g.pageid === a.pageid)
    );

    gathered.push(...filtered);

    if (gathered.length >= count) break;
  }

  // If not enough articles found, return what we have
  if (gathered.length < 5 && gathered.length > 0) {
    console.warn(`Only found ${gathered.length} articles, returning anyway`);
    return { articles: gathered };
  }
  
  // If we have at least half of what we need, that's good enough
  if (gathered.length >= Math.ceil(count / 2)) {
    console.log(`Found ${gathered.length} articles (target: ${count})`);
  }

  // Shuffle all gathered articles for variety
  gathered.sort(() => Math.random() - 0.5);
  const topQuality = gathered.slice(0, 2);
  const rest = gathered.slice(2);
  
  // VARIETY CONTROL: Prevent same themes repeating
  const diversified: WikipediaArticle[] = [];
  const recentThemes: string[] = [];
  
  for (const article of rest) {
    // Determine article theme based on title/description
    let articleTheme = 'other';
    const text = `${article.title} ${article.description || ''}`.toLowerCase();
    
    for (const [theme, keywords] of Object.entries({
      crypto: ['crypto', 'bitcoin', 'blockchain'],
      sports: ['football', 'basketball', 'nba', 'premier league', 'tennis'],
      fitness: ['fitness', 'bodybuilding', 'workout', 'gym', 'training'],
      tech: ['ai', 'technology', 'smartphone', 'app'],
      business: ['billionaire', 'ceo', 'company', 'entrepreneur'],
      celebrity: ['actor', 'singer', 'rapper', 'influencer'],
      climate: ['climate', 'environment', 'sustainable'],
      mentalhealth: ['mental health', 'therapy', 'anxiety'],
      theory: ['theory', 'paradox', 'philosophy', 'conspiracy'],
      science: ['experiment', 'discovery', 'quantum', 'physics', 'chemistry'],
      space: ['nasa', 'astronaut', 'planet', 'galaxy', 'rocket', 'spacex'],
      history: ['ancient', 'empire', 'medieval', 'war', 'civilization'],
      animals: ['animal', 'species', 'predator', 'wildlife', 'dinosaur'],
      food: ['cuisine', 'chef', 'restaurant', 'dish', 'cooking'],
      music: ['album', 'band', 'concert', 'songwriter', 'rock', 'pop'],
      movies: ['film', 'movie', 'director', 'oscar', 'series'],
      gaming: ['video game', 'esports', 'nintendo', 'playstation', 'xbox'],
      art: ['painting', 'sculptor', 'gallery', 'museum', 'masterpiece'],
      psychology: ['cognitive', 'personality', 'behavior', 'perception'],
      medicine: ['vaccine', 'disease', 'surgery', 'pandemic', 'hospital'],
      geography: ['volcano', 'earthquake', 'mountain', 'canyon', 'desert'],
      mythology: ['mythology', 'legend', 'myth', 'folklore', 'god'],
      cars: ['supercar', 'motorsport', 'racing', 'engine', 'formula'],
      military: ['military', 'army', 'battle', 'special forces', 'tank'],
      ocean: ['ocean', 'marine', 'shipwreck', 'submarine', 'coral'],
      drama: ['murder', 'scandal', 'crime', 'heist', 'serial killer'],
      travel: ['tourist', 'island', 'beach', 'landmark', 'castle'],
      lifestyle: ['luxury', 'fashion', 'designer', 'brand', 'rolex'],
      internet: ['meme', 'viral', 'tiktok', 'youtube', 'podcast'],
      career: ['university', 'degree', 'career', 'education', 'scholarship']
    })) {
      if (keywords.some(kw => text.includes(kw))) {
        articleTheme = theme;
        break;
      }
    }
    
    // Check if this theme appeared in last 2 articles
    const shouldSkip = recentThemes.slice(-2).includes(articleTheme) && articleTheme !== 'other';
    
    if (!shouldSkip) {
      diversified.push(article);
      recentThemes.push(articleTheme);
      if (recentThemes.length > 5) recentThemes.shift();
    }
  }
  
  // Add skipped articles at the end
  const skipped = rest.filter(a => !diversified.includes(a));
  
  const final = [...topQuality, ...diversified, ...skipped].slice(0, count);

  return { articles: final };
};

export const fetchArticleByTitle = async (title: string): Promise<WikipediaArticle | null> => {
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'pageimages|extracts|info|pageprops',
    piprop: 'thumbnail',
    pithumbsize: '1200',
    exintro: '1',
    explaintext: '1',
    exchars: '500',
    inprop: 'url',
    format: 'json',
    origin: '*'
  });

  try {
    const response = await fetch(`${EN_API_URL}?${params.toString()}`, {
      signal: AbortSignal.timeout(5000)
    });
    const data = await response.json();
    if (!data.query || !data.query.pages) return null;

    const page: any = Object.values(data.query.pages)[0];
    if (!page || page.missing !== undefined) return null;

    return {
      pageid: page.pageid,
      title: page.title,
      displaytitle: page.title,
      thumbnail: page.thumbnail,
      extract: page.extract || '',
      extract_html: page.extract || '',
      description: page.pageprops?.['wikibase-description'] || '',
      content_urls: {
        desktop: { page: page.fullurl || `https://en.wikipedia.org/wiki/${encodeURIComponent(page.title)}` },
        mobile: { page: (page.fullurl || `https://en.wikipedia.org/wiki/${encodeURIComponent(page.title)}`).replace('en.wikipedia', 'en.m.wikipedia') }
      }
    };
  } catch (e) {
    return null;
  }
};

export const updateInterestProfile = (
  currentProfile: InterestProfile,
  likedArticle: WikipediaArticle
): InterestProfile => {
  const newProfile = { ...currentProfile };
  
  const text = `${likedArticle.title} ${likedArticle.description || ''} ${likedArticle.extract || ''}`.toLowerCase();
  
  // Match against our theme categories
  for (const [theme, categories] of Object.entries(CATEGORY_THEMES)) {
    const themeKeywords = categories.map(c => c.toLowerCase().replace(/_/g, ' '));
    
    const matches = themeKeywords.some(keyword => 
      text.includes(keyword)
    );
    
    if (matches) {
      categories.forEach(cat => {
        newProfile[cat] = (newProfile[cat] || 0) + 1;
      });
    }
  }
  
  // Specific boosting based on content
  if (text.includes('billionaire') || text.includes('ceo') || text.includes('entrepreneur')) {
    newProfile['Billionaires'] = (newProfile['Billionaires'] || 0) + 2;
    newProfile['Companies'] = (newProfile['Companies'] || 0) + 1;
  }
  if (text.includes('crypto') || text.includes('bitcoin') || text.includes('blockchain')) {
    newProfile['Cryptocurrencies'] = (newProfile['Cryptocurrencies'] || 0) + 2;
  }
  if (text.includes('football') || text.includes('soccer') || text.includes('premier league')) {
    newProfile['Premier_League'] = (newProfile['Premier_League'] || 0) + 2;
    newProfile['Footballers'] = (newProfile['Footballers'] || 0) + 1;
  }
  if (text.includes('nba') || text.includes('basketball')) {
    newProfile['NBA'] = (newProfile['NBA'] || 0) + 2;
    newProfile['Basketball_players'] = (newProfile['Basketball_players'] || 0) + 1;
  }
  if (text.includes('rapper') || text.includes('hip hop') || text.includes('music')) {
    newProfile['Rappers'] = (newProfile['Rappers'] || 0) + 2;
    newProfile['Singers'] = (newProfile['Singers'] || 0) + 1;
  }
  if (text.includes('fitness') || text.includes('bodybuilding') || text.includes('workout')) {
    newProfile['Fitness'] = (newProfile['Fitness'] || 0) + 2;
    newProfile['Bodybuilding'] = (newProfile['Bodybuilding'] || 0) + 1;
  }
  if (text.includes('theory') || text.includes('paradox') || text.includes('philosophy')) {
    newProfile['Conspiracy_theories'] = (newProfile['Conspiracy_theories'] || 0) + 1;
    newProfile['Philosophy'] = (newProfile['Philosophy'] || 0) + 2;
  }
  if (text.includes('mental health') || text.includes('therapy') || text.includes('psychology')) {
    newProfile['Mental_health'] = (newProfile['Mental_health'] || 0) + 2;
    newProfile['Psychology'] = (newProfile['Psychology'] || 0) + 1;
  }
  if (text.includes('climate') || text.includes('sustainable') || text.includes('environment')) {
    newProfile['Climate_change'] = (newProfile['Climate_change'] || 0) + 2;
    newProfile['Sustainability'] = (newProfile['Sustainability'] || 0) + 1;
  }
  if (text.includes('ai') || text.includes('artificial intelligence') || text.includes('machine learning')) {
    newProfile['Artificial_intelligence'] = (newProfile['Artificial_intelligence'] || 0) + 2;
    newProfile['Machine_learning'] = (newProfile['Machine_learning'] || 0) + 1;
  }
  if (text.includes('career') || text.includes('university') || text.includes('education')) {
    newProfile['Career_development'] = (newProfile['Career_development'] || 0) + 2;
    newProfile['Universities'] = (newProfile['Universities'] || 0) + 1;
  }
  if (text.includes('influencer') || text.includes('tiktok') || text.includes('content creator')) {
    newProfile['Influencers'] = (newProfile['Influencers'] || 0) + 2;
    newProfile['Internet_culture'] = (newProfile['Internet_culture'] || 0) + 1;
  }
  if (text.includes('experiment') || text.includes('discovery') || text.includes('quantum') || text.includes('physics')) {
    newProfile['Scientific_discoveries'] = (newProfile['Scientific_discoveries'] || 0) + 2;
    newProfile['Physics'] = (newProfile['Physics'] || 0) + 1;
  }
  if (text.includes('nasa') || text.includes('astronaut') || text.includes('space') || text.includes('rocket')) {
    newProfile['Space_exploration'] = (newProfile['Space_exploration'] || 0) + 2;
    newProfile['NASA'] = (newProfile['NASA'] || 0) + 1;
  }
  if (text.includes('ancient') || text.includes('empire') || text.includes('medieval') || text.includes('civilization')) {
    newProfile['Ancient_history'] = (newProfile['Ancient_history'] || 0) + 2;
    newProfile['Empires'] = (newProfile['Empires'] || 0) + 1;
  }
  if (text.includes('animal') || text.includes('species') || text.includes('predator') || text.includes('wildlife')) {
    newProfile['Animals'] = (newProfile['Animals'] || 0) + 2;
    newProfile['Mammals'] = (newProfile['Mammals'] || 0) + 1;
  }
  if (text.includes('cuisine') || text.includes('food') || text.includes('chef') || text.includes('restaurant')) {
    newProfile['Cuisine'] = (newProfile['Cuisine'] || 0) + 2;
    newProfile['Restaurants'] = (newProfile['Restaurants'] || 0) + 1;
  }
  if (text.includes('album') || text.includes('band') || text.includes('songwriter') || text.includes('concert')) {
    newProfile['Musicians'] = (newProfile['Musicians'] || 0) + 2;
    newProfile['Music_festivals'] = (newProfile['Music_festivals'] || 0) + 1;
  }
  if (text.includes('film') || text.includes('movie') || text.includes('director') || text.includes('oscar')) {
    newProfile['Films'] = (newProfile['Films'] || 0) + 2;
    newProfile['Television_series'] = (newProfile['Television_series'] || 0) + 1;
  }
  if (text.includes('video game') || text.includes('esports') || text.includes('nintendo') || text.includes('playstation')) {
    newProfile['Video_games'] = (newProfile['Video_games'] || 0) + 2;
    newProfile['Esports'] = (newProfile['Esports'] || 0) + 1;
  }
  if (text.includes('painting') || text.includes('sculptor') || text.includes('gallery') || text.includes('masterpiece')) {
    newProfile['Painters'] = (newProfile['Painters'] || 0) + 2;
    newProfile['Art_movements'] = (newProfile['Art_movements'] || 0) + 1;
  }
  if (text.includes('cognitive') || text.includes('personality') || text.includes('behavior') || text.includes('perception')) {
    newProfile['Psychology'] = (newProfile['Psychology'] || 0) + 2;
    newProfile['Cognitive_science'] = (newProfile['Cognitive_science'] || 0) + 1;
  }
  if (text.includes('vaccine') || text.includes('disease') || text.includes('surgery') || text.includes('pandemic')) {
    newProfile['Medicine'] = (newProfile['Medicine'] || 0) + 2;
    newProfile['Diseases'] = (newProfile['Diseases'] || 0) + 1;
  }
  if (text.includes('volcano') || text.includes('earthquake') || text.includes('canyon') || text.includes('glacier')) {
    newProfile['Volcanoes'] = (newProfile['Volcanoes'] || 0) + 2;
    newProfile['Mountains'] = (newProfile['Mountains'] || 0) + 1;
  }
  if (text.includes('mythology') || text.includes('legend') || text.includes('myth') || text.includes('folklore')) {
    newProfile['Mythology'] = (newProfile['Mythology'] || 0) + 2;
    newProfile['Greek_mythology'] = (newProfile['Greek_mythology'] || 0) + 1;
  }
  if (text.includes('supercar') || text.includes('motorsport') || text.includes('horsepower') || text.includes('formula one')) {
    newProfile['Supercars'] = (newProfile['Supercars'] || 0) + 2;
    newProfile['Cars'] = (newProfile['Cars'] || 0) + 1;
  }
  if (text.includes('military') || text.includes('army') || text.includes('battle') || text.includes('special forces')) {
    newProfile['Military_history'] = (newProfile['Military_history'] || 0) + 2;
    newProfile['Battles'] = (newProfile['Battles'] || 0) + 1;
  }
  if (text.includes('ocean') || text.includes('marine') || text.includes('shipwreck') || text.includes('submarine')) {
    newProfile['Oceans'] = (newProfile['Oceans'] || 0) + 2;
    newProfile['Marine_biology'] = (newProfile['Marine_biology'] || 0) + 1;
  }

  return newProfile;
};

// Negative scoring: reduce interest in categories matching dismissed article
export const downvoteInterestProfile = (
  currentProfile: InterestProfile,
  article: WikipediaArticle
): InterestProfile => {
  const newProfile = { ...currentProfile };
  const text = `${article.title} ${article.description || ''} ${article.extract || ''}`.toLowerCase();

  for (const [theme, categories] of Object.entries(CATEGORY_THEMES)) {
    const themeKeywords = categories.map(c => c.toLowerCase().replace(/_/g, ' '));
    if (themeKeywords.some(keyword => text.includes(keyword))) {
      categories.forEach(cat => {
        newProfile[cat] = Math.max(0, (newProfile[cat] || 0) - 2);
      });
    }
  }

  return newProfile;
};

export const processViewSignals = (
  currentProfile: InterestProfile,
  signals: ViewSignal[]
): InterestProfile => {
  const newProfile = { ...currentProfile };

  for (const signal of signals) {
    const durationSec = signal.duration / 1000;

    // Skip articles viewed for less than 5 seconds
    if (durationSec < 5) continue;

    // Duration weight tiers
    let weight: number;
    if (durationSec < 15) {
      weight = 0.3;
    } else if (durationSec < 30) {
      weight = 0.6;
    } else {
      weight = 1.0;
    }

    // Modal bonus
    const modalSec = signal.modalDuration / 1000;
    let modalBonus = 0;
    if (modalSec > 0) {
      modalBonus = modalSec >= 30 ? 1.0 : 0.5;
    }

    const text = `${signal.article.title} ${signal.article.description || ''} ${signal.article.extract || ''}`.toLowerCase();

    // Match against theme categories (same as updateInterestProfile)
    for (const [theme, categories] of Object.entries(CATEGORY_THEMES)) {
      const themeKeywords = categories.map(c => c.toLowerCase().replace(/_/g, ' '));

      const matches = themeKeywords.some(keyword => text.includes(keyword));

      if (matches) {
        categories.forEach(cat => {
          newProfile[cat] = Math.min((newProfile[cat] || 0) + weight, 50);
        });
      }
    }

    // Specific keyword boosting (scaled by weight, lighter than likes)
    const boosts: [string[], Record<string, number>][] = [
      [['billionaire', 'ceo', 'entrepreneur'], { 'Billionaires': 2, 'Companies': 1 }],
      [['crypto', 'bitcoin', 'blockchain'], { 'Cryptocurrencies': 2 }],
      [['football', 'soccer', 'premier league'], { 'Premier_League': 2, 'Footballers': 1 }],
      [['nba', 'basketball'], { 'NBA': 2, 'Basketball_players': 1 }],
      [['rapper', 'hip hop', 'music'], { 'Rappers': 2, 'Singers': 1 }],
      [['fitness', 'bodybuilding', 'workout'], { 'Fitness': 2, 'Bodybuilding': 1 }],
      [['theory', 'paradox', 'philosophy'], { 'Conspiracy_theories': 1, 'Philosophy': 2 }],
      [['mental health', 'therapy', 'psychology'], { 'Mental_health': 2, 'Psychology': 1 }],
      [['climate', 'sustainable', 'environment'], { 'Climate_change': 2, 'Sustainability': 1 }],
      [['ai', 'artificial intelligence', 'machine learning'], { 'Artificial_intelligence': 2, 'Machine_learning': 1 }],
      [['career', 'university', 'education'], { 'Career_development': 2, 'Universities': 1 }],
      [['influencer', 'tiktok', 'content creator'], { 'Influencers': 2, 'Internet_culture': 1 }],
      [['experiment', 'discovery', 'quantum', 'physics'], { 'Scientific_discoveries': 2, 'Physics': 1 }],
      [['nasa', 'astronaut', 'space', 'rocket'], { 'Space_exploration': 2, 'NASA': 1 }],
      [['ancient', 'empire', 'medieval', 'civilization'], { 'Ancient_history': 2, 'Empires': 1 }],
      [['animal', 'species', 'predator', 'wildlife'], { 'Animals': 2, 'Mammals': 1 }],
      [['cuisine', 'food', 'chef', 'restaurant'], { 'Cuisine': 2, 'Restaurants': 1 }],
      [['album', 'band', 'songwriter', 'concert'], { 'Musicians': 2, 'Music_festivals': 1 }],
      [['film', 'movie', 'director', 'oscar'], { 'Films': 2, 'Television_series': 1 }],
      [['video game', 'esports', 'nintendo', 'playstation'], { 'Video_games': 2, 'Esports': 1 }],
      [['painting', 'sculptor', 'gallery', 'masterpiece'], { 'Painters': 2, 'Art_movements': 1 }],
      [['cognitive', 'personality', 'behavior', 'perception'], { 'Psychology': 2, 'Cognitive_science': 1 }],
      [['vaccine', 'disease', 'surgery', 'pandemic'], { 'Medicine': 2, 'Diseases': 1 }],
      [['volcano', 'earthquake', 'canyon', 'glacier'], { 'Volcanoes': 2, 'Mountains': 1 }],
      [['mythology', 'legend', 'myth', 'folklore'], { 'Mythology': 2, 'Greek_mythology': 1 }],
      [['supercar', 'motorsport', 'horsepower', 'formula one'], { 'Supercars': 2, 'Cars': 1 }],
      [['military', 'army', 'battle', 'special forces'], { 'Military_history': 2, 'Battles': 1 }],
      [['ocean', 'marine', 'shipwreck', 'submarine'], { 'Oceans': 2, 'Marine_biology': 1 }],
    ];

    for (const [keywords, categoryBoosts] of boosts) {
      if (keywords.some(kw => text.includes(kw))) {
        for (const [cat, baseBoost] of Object.entries(categoryBoosts)) {
          const increment = baseBoost * weight + (baseBoost * modalBonus * 0.5);
          newProfile[cat] = Math.min((newProfile[cat] || 0) + increment, 50);
        }
      }
    }
  }

  return newProfile;
};
